// Copyright ©️ 2026 eVolve MEP, LLC

import { useMemo, useState, useEffect } from 'react';

import type { StrongOmit } from '@evolvemep/api-ts';
import { useCombobox, type Primitive } from '@mantine/core';
import { useUncontrolled } from '@mantine/hooks';

import { getSelectOptionLabel, getSelectOptions } from 'components/Mantine/TypeSafeSelect/common';
import { useCreatable } from 'components/Mantine/TypeSafeSelect/Creatables/useCreatable';
import type { WrappedMultiSelectProps, WrappedSelectProps } from 'components/Mantine/TypeSafeSelect/types';
import { useSelectRightSection } from 'components/Mantine/TypeSafeSelect/useSelectRightSection';
import { isNotNil } from 'helpers/isNotNil';
import { strongKeys } from 'helpers/strongEntries';

type Props<T extends Primitive, D extends object = object> = StrongOmit<
  WrappedSelectProps<T, D> & WrappedMultiSelectProps<T, D>,
  // `hidePickedOptions` is `never` on the single-select side and `boolean` on the multi-select side;
  // intersecting collapses it to `never`, so omit it here and re-declare it as the boolean it actually is.
  'value' | 'defaultValue' | 'onChange' | 'vars' | 'hidePickedOptions'
> & {
  hidePickedOptions?: boolean;
};

export type TypeSafeInternalValue<T> =
  | {
      type: 'single';
      __value: T | null;
      __onChange: (value: T | null) => void;
    }
  | {
      type: 'multi';
      __value: T[];
      __onChange: (value: T[]) => void;
    };

export const useWrappedSelect = <T extends Primitive, D extends object = object>(
  props: TypeSafeInternalValue<T>,
  selectProps: Props<T, D>,
) => {
  const {
    dropdownOpened,
    onDropdownOpen,
    onDropdownClose,
    creatable,
    clearSearchOnFocus = !creatable,
    limit = 1000,
    hidePickedOptions = props.type === 'multi',
    noFilterOnSearch,
    onClear,
    searchValue,
    onSearchChange,
    createdValue,
    alwaysShowWhenLoading: _,
    ...rest
  } = selectProps;

  const [searchPlaceholder, setSearchPlaceholder] = useState('');
  const [__searchValue, __onSearchChange] = useUncontrolled({
    value: searchValue,
    defaultValue: '',
    onChange: (v) => {
      setSearchPlaceholder(v);
      onSearchChange?.(v);
    },
  });

  const overrideProps = useCreatable({ __searchValue, __onSearchChange }, selectProps);
  const { data, renderOption } = overrideProps;

  const combobox = useCombobox({
    opened: dropdownOpened,
    defaultOpened: dropdownOpened,
    onDropdownOpen: () => {
      combobox.updateSelectedOptionIndex('active');
      onDropdownOpen?.();
      if (!clearSearchOnFocus) {
        if (createdValue) {
          setSearchPlaceholder(createdValue);
        } else if (isNotNil(props.__value) && !Array.isArray(props.__value)) {
          setSearchPlaceholder(getSelectOptionLabel(data, props.__value) ?? __searchValue);
        }
      }
    },
    onDropdownClose: () => {
      combobox.resetSelectedOption();
      onDropdownClose?.();
      __onSearchChange('');
    },
  });

  const options = useMemo(
    () =>
      getSelectOptions({
        data,
        limit,
        value: props.__value,
        renderOption,
        searchValue: __searchValue,
        hidePickedOptions,
        noFilterOnSearch,
      }),
    [data, limit, props.__value, renderOption, __searchValue, hidePickedOptions, noFilterOnSearch],
  );

  useEffect(() => {
    if (__searchValue) {
      combobox.selectFirstOption();
    } else {
      combobox.resetSelectedOption();
    }
  }, [combobox, __searchValue]);

  const selectRightSection = useSelectRightSection(
    {
      dropdownOpened: combobox.dropdownOpened,
      hasValue: props.type === 'multi' ? props.__value.length > 0 : isNotNil(props.__value),
      onClear: () => {
        if (props.type === 'multi') {
          props.__onChange([] as T[]);
        } else {
          props.__onChange(null);
        }
        __onSearchChange('');
        onClear?.();
      },
    },
    selectProps,
  );

  const overrideKeys = useMemo(() => {
    const restKeys = Object.keys(rest);
    const useWrappedSelectKeys = Object.keys(selectProps).filter((k) => !restKeys.includes(k));
    return [...strongKeys(overrideProps), ...useWrappedSelectKeys] as unknown as (keyof Props<T, D>)[];
  }, [overrideProps, rest, selectProps]);

  return {
    searchPlaceholder,
    __searchValue,
    __onSearchChange,
    combobox,
    options,
    selectRightSection,
    /**
     * This is the list of keys used by the `useWrappedSelect` hook,
     * and therefore should not be spread onto the WrappedSelect/WrappedMultiSelect
     */
    overrideKeys,
    overrideProps,
  };
};
